/**
 * Client-side shape of an inbound pathology result that arrived without a MedicAI lab order.
 * Mirrors the server's `UnlinkedResultDto` — see tasks/unlinked-lab-results.md.
 */

import { VendorDoctors } from './vendor-doctors.model';

export type UnlinkedResolutionState =
  | 'unmatched'
  | 'auto_linked'
  | 'manually_linked'
  | 'dismissed';

export interface UnlinkedResultDemographics {
  surname?: string | null;
  firstName?: string | null;
  title?: string | null;
  idNumber?: string | null;
  passportNumber?: string | null;
  dateOfBirth?: string | null;
  sex?: string | null;
}

export interface UnlinkedResultDto {
  id: string;
  receivedAt?: string | null;
  vendor?: string | null;
  specimenNumber?: string | null;
  reportType?: string | null;
  specimenStatus?: string | null;
  orderNoRaw?: string | null;
  resolutionState?: UnlinkedResolutionState | null;
  orderingDoctorCode?: string | null;
  recipientCode?: string | null;
  doctorName?: string | null;
  doctorHpcsa?: string | null;
  doctorPracticeNumber?: string | null;
  resolvedUserId?: string | null;
  groupId?: string | null;
  patientId?: string | null;
  patientLabOrderId?: string | null;
  receiveCount?: number | null;
  sequenceNumber?: number | null;
  demographics?: UnlinkedResultDemographics | null;
  /**
   * The four parties the lab named on the payload, with the lab's own role on each.
   *
   * NULLABLE, and null on every row that predates the column — which is currently every row.
   * Each of the four slots is independently nullable too. See `vendor-doctors.model.ts`; the
   * code to suppress arrives on the ENVELOPE as `vendorDoctorPlatformCode`, not here.
   */
  vendorDoctors?: VendorDoctors | null;
}

/**
 * The unlinked endpoints are ONE-BASED, like `GET /results/group/:groupId`.
 * Page 0 is not the first page.
 */
export const UNLINKED_FIRST_PAGE = 1;

/** `{ success, data }` envelope used by every clinical endpoint. */
export interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * Error bodies carry `error` / `message` and have NO `data` key — never read `.data`
 * off a failed response.
 */
export interface ApiErrorBody {
  success: false;
  error?: string;
  message?: string;
  details?: unknown;
}

/**
 * 409 refusals the link/dismiss endpoints raise when the row has already been resolved,
 * typically because someone else acted on it first and this tab is stale.
 */
export const UNLINKED_CONFLICT_MESSAGES: Record<string, string> = {
  UNLINKED_RESULT_ALREADY_LINKED:
    'This result has already been linked to a patient, so it can no longer be dismissed. The list has been refreshed.',
  UNLINKED_RESULT_DISMISSED:
    'This result has already been dismissed, so it can no longer be linked. The list has been refreshed.',
};

/** Maps an HttpErrorResponse body to a message, falling back to whatever the server said. */
export function unlinkedErrorMessage(error: any, fallback: string): string {
  const body: ApiErrorBody | undefined = error?.error;
  const code = typeof body?.error === 'string' ? body.error : undefined;
  if (code && UNLINKED_CONFLICT_MESSAGES[code]) {
    return UNLINKED_CONFLICT_MESSAGES[code];
  }
  const message = body?.message ?? error?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/** True when the failure means this client's copy of the row is stale and must be reloaded. */
export function isUnlinkedConflict(error: any): boolean {
  const code = error?.error?.error;
  return error?.status === 409
    || (typeof code === 'string' && Object.prototype.hasOwnProperty.call(UNLINKED_CONFLICT_MESSAGES, code));
}

export interface UnlinkedResultPage extends ApiEnvelope<UnlinkedResultDto[]> {
  total?: number;
  page?: number;
  pageSize?: number;
  /**
   * OUR OWN vendor code, echoed by the server so the client knows which badge to drop.
   * ENVELOPE-level: one per response, never per row, and never hard-coded in the client.
   */
  vendorDoctorPlatformCode?: string | null;
}

/**
 * `GET /api/results/group/:groupId`. The rows are still `any` — the Results list has never had
 * a row model and retrofitting one is not this change — but the envelope is typed, because the
 * platform code lives on it and reading it off a row would silently yield `undefined`.
 */
export interface LabOrderResultsPage extends ApiEnvelope<any[]> {
  vendorDoctorPlatformCode?: string | null;
}

export interface UnlinkedLinkResult {
  id: string;
  patientId: string;
  patientLabOrderId?: string | null;
  resolutionState: UnlinkedResolutionState;
}

export interface UnlinkedDismissResult {
  id: string;
  resolutionState: UnlinkedResolutionState;
}

export interface UnlinkedPayload {
  id: string;
  rawPayload: string;
}

/**
 * The unlinked endpoints are ONE-BASED: the server computes `offset = (page - 1) * pageSize`
 * and defaults to `page=1`, matching `GET /results/group/:groupId`. Page 0 would re-request
 * a negative offset, so the first page is 1.
 *
 * Returns every page number to request, given the `total` the first response reported.
 * Deterministic: no recursing until a short page comes back.
 */
export function unlinkedPageNumbers(total: number, pageSize: number): number[] {
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(pageSize) || pageSize <= 0) {
    return [UNLINKED_FIRST_PAGE];
  }
  const pageCount = Math.ceil(total / pageSize);
  const pages: number[] = [];
  for (let page = UNLINKED_FIRST_PAGE; page < UNLINKED_FIRST_PAGE + pageCount; page++) {
    pages.push(page);
  }
  return pages;
}

// ---------------------------------------------------------------------------
// Viewing one unlinked result (GET .../unlinked/:id/results)
// ---------------------------------------------------------------------------

/**
 * One unlinked result with its parsed document.
 *
 * `labResults` is deliberately the SAME shape as `PatientLabOrder.labResults`
 * (`{ vendor, processedAt, specimens[], summary }`), so `LabResultsDialogComponent`
 * renders it with no special-casing.
 */
export interface UnlinkedResultDetail {
  id: string;
  specimenNumber?: string | null;
  reportType?: string | null;
  specimenStatus?: string | null;
  receivedAt?: string | null;
  /**
   * THREE-STATE, and the three states mean different things:
   *   true  - parsed, and the document contains test results
   *   false - parsed, and the document genuinely contains no test results
   *           (comment-only reports such as blood gases; ~9% of live payloads)
   *   null  - no parser ever ran, so nothing is known about the contents
   * `false` is a successfully read document. `null` is an unread one. Never collapse
   * them with `!hasResults`.
   */
  hasResults?: boolean | null;
  parsedResultCount?: number | null;
  demographics?: UnlinkedResultDemographics | null;
  labResults?: any | null;
}

/** What the results pane should render for a read-only unlinked result. */
export type UnlinkedResultsDisplayState = 'results' | 'document-no-tests' | 'not-parsed';

/**
 * Maps the three-state `hasResults` plus what actually came back to a render decision.
 *
 * `hasResults === false` is NOT a failure: it is a real document that simply carries no
 * discrete tests, and it must render the document header rather than an empty pane that
 * reads as an error. `hasResults == null` is a different thing again - nobody has parsed
 * it, so we know nothing about the contents.
 */
export function unlinkedResultsDisplayState(
  hasResults: boolean | null | undefined,
  renderableTestCount: number
): UnlinkedResultsDisplayState {
  if (hasResults === null || hasResults === undefined) {
    return 'not-parsed';
  }
  if (hasResults === false) {
    return 'document-no-tests';
  }
  // hasResults === true. If the parser claims results but nothing renderable survived,
  // still show the document rather than an empty table.
  return Number(renderableTestCount) > 0 ? 'results' : 'document-no-tests';
}

/** Heading/body copy for the two non-rendering states. Kept pure so it can be asserted. */
export function unlinkedResultsStateCopy(
  state: UnlinkedResultsDisplayState
): { title: string; detail: string } {
  if (state === 'not-parsed') {
    return {
      title: 'Report not processed',
      detail: 'This report has not been processed into individual test results, so there is nothing '
        + 'to show here. The original document is still available - download the PDF, or open the raw '
        + 'payload from the Unlinked list.'
    };
  }
  if (state === 'document-no-tests') {
    return {
      title: 'Report received - no individual test results',
      detail: 'The laboratory sent this report and it was read successfully, but it carries no discrete '
        + 'test results. Comment-only reports such as blood gas reports normally look like this. '
        + 'Download the PDF to read the full document.'
    };
  }
  return { title: '', detail: '' };
}

/** Characters that are illegal in a Windows/macOS file name. Lancet specimen numbers contain colons. */
const ILLEGAL_FILE_NAME_CHARS = /[<>:"/\\|?*]/g;

/** Makes one path segment safe to hand to a browser download. */
export function sanitizeDownloadFileNamePart(part: string): string {
  return String(part == null ? '' : part)
    .replace(ILLEGAL_FILE_NAME_CHARS, '-')
    .replace(/\s+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

/**
 * Download name for an unlinked report, mirroring `lab-results-<orderNumber>.pdf`.
 * Specimen numbers may contain colons (e.g. `24:1234567`), which Windows rejects and
 * Chrome silently rewrites, so they are replaced here rather than left to the browser.
 */
export function unlinkedPdfFileName(specimenNumber: string | null | undefined, id: string): string {
  const cleaned = sanitizeDownloadFileNamePart(specimenNumber == null ? '' : specimenNumber);
  const fallback = sanitizeDownloadFileNamePart(String(id == null ? '' : id).substring(0, 8));
  const base = cleaned || fallback || 'unknown';
  return `lab-results-${base}.pdf`;
}

/**
 * `responseType: 'blob'` requests hand back a Blob for error bodies too, so there is no
 * readable `{ error, message }` in them. Use the caller's own wording for those instead of
 * leaking "Http failure response for ...".
 */
export function unlinkedBlobErrorMessage(error: any, fallback: string): string {
  const body = error?.error;
  const looksLikeBlob = !!body && typeof body === 'object'
    && typeof body.size === 'number' && typeof body.type === 'string';
  if (looksLikeBlob) {
    return fallback;
  }
  return unlinkedErrorMessage(error, fallback);
}

// ---------------------------------------------------------------------------
// Read-only gating for LabResultsDialogComponent
// ---------------------------------------------------------------------------

/**
 * What `LabResultsDialogComponent` is allowed to do. Every patient-bound or outbound
 * capability is off for an unlinked result: there is no patient to attach a note to,
 * no address to mail, and no PatientLabOrder to flag or mark reviewed.
 *
 * This is the single source of truth - the template gates the Cura tab on
 * `caps.curaAssistant`, and `markAsReviewedIfNeeded()` gates on `caps.autoMarkReviewed`.
 * Do not re-derive these from `readOnly` at the call sites.
 */
export interface LabResultsDialogCapabilities {
  /** Cura AI tab. `sendMessage` posts `patientId`, so it cannot work without a patient. */
  curaAssistant: boolean;
  /** Emailing a generated summary out of the system. */
  email: boolean;
  /** WhatsApp chat dialog - addressed to a patient. */
  whatsapp: boolean;
  /** Flag/unflag, which PATCHes a PatientLabOrder. */
  flag: boolean;
  /** The silent "mark reviewed on open" side effect, which PATCHes a PatientLabOrder. */
  autoMarkReviewed: boolean;
  /** The clickable patient name in the header. */
  patientFileLink: boolean;
  /** Downloading the report PDF stays available in every mode. */
  download: boolean;
}

/** `stateUnit`: a state unit (tasks/state-mode.md) sends nothing outside the system, so no email or WhatsApp. */
export function labResultsDialogCapabilities(readOnly: boolean, stateUnit = false): LabResultsDialogCapabilities {
  const patientBound = readOnly !== true;
  return {
    curaAssistant: patientBound,
    email: patientBound && !stateUnit,
    whatsapp: patientBound && !stateUnit,
    flag: patientBound,
    autoMarkReviewed: patientBound,
    patientFileLink: patientBound,
    download: true
  };
}
